import { AlertTriangle, ShieldCheck } from 'lucide-react';

import { STATUS_TONE } from '@/nucleo/statusBadgeTokens';
import { situacaoDeValidade, JANELA_DE_AVISO_EM_DIAS } from '@/nucleo/validade';
import { Badge } from '@/ui/base/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/ui/base/tooltip';

/**
 * Uma coluna que responde "posso usar este recurso hoje?".
 *
 * O PROBLEMA QUE ELA RESOLVE. Um condutor tem seis datas de validade — CNH, CRMC,
 * primeiros socorros, transporte escolar, mobilidade reduzida, atestado medico —,
 * mais a situacao cadastral e o vinculo com o credenciado. Oito colunas para uma
 * pergunta so, e quem varre a lista tem de cruzar as oito de cabeca, linha por linha,
 * sabendo que esquecer uma significa mandar para a rua alguem sem documento valido.
 *
 * Aqui isso vira um selo com dois niveis, e a diferenca entre eles e operacional:
 *
 * - CRITICO (vermelho): bloqueia. Documento JA vencido, ou situacao suspensa ou
 *   bloqueada. Nao entra em ordem de servico nova ate regularizar.
 * - ATENCAO (ambar): acompanha. Documento vencendo dentro da janela, inativo, sem
 *   vinculo, ou pendencia financeira. Ainda opera, mas tem prazo.
 *
 * O SELO VERDE NAO E ENFEITE: sem ele, "sem problema" e indistinguivel de "a coluna
 * nao carregou". Uma celula vazia numa tabela de conformidade e ambigua exatamente no
 * caso em que a clareza importa mais.
 *
 * A COR NUNCA VIAJA SOZINHA. Cada nivel tem icone proprio e texto proprio, porque
 * quem nao distingue vermelho de ambar precisa da mesma informacao
 * (CLAUDE.md, "Regras de interface", item 5).
 */

type Gravidade = 'regular' | 'atencao' | 'critica';

interface IrregularidadeCellProps {
  /** Datas de validade a conferir (ISO). Nulas e vazias sao ignoradas. */
  validades?: readonly (string | null | undefined)[];
  /** Situacao cadastral da entidade. */
  situacao?: string | null;
  /**
   * `true` quando a ausencia de credenciado conta como pendencia. So passe onde o
   * vinculo e esperado — monitor nao tem vinculo direto, e cobrar isso dele
   * marcaria a lista inteira de ambar sem motivo.
   */
  semVinculo?: boolean;
  /** Pendencia financeira. Só credenciado tem. */
  pendenciaFinanceira?: string | null;
}

export function IrregularidadeCell({
  validades = [],
  situacao,
  semVinculo,
  pendenciaFinanceira,
}: IrregularidadeCellProps) {
  const motivos: string[] = [];
  let gravidade: Gravidade = 'regular';

  const situacaoNormalizada = situacao?.toUpperCase();

  if (situacaoNormalizada === 'SUSPENSO' || situacaoNormalizada === 'BLOQUEADO') {
    motivos.push(situacaoNormalizada === 'SUSPENSO' ? 'Suspenso' : 'Bloqueado');
    gravidade = 'critica';
  } else if (situacaoNormalizada === 'INATIVO') {
    motivos.push('Inativo');
    if (gravidade === 'regular') {
      gravidade = 'atencao';
    }
  }

  let vencidos = 0;
  let vencendo = 0;
  for (const validade of validades) {
    const estado = situacaoDeValidade(validade);
    if (estado === 'vencido') {
      vencidos += 1;
    } else if (estado === 'vencendo') {
      vencendo += 1;
    }
  }

  if (vencidos > 0) {
    motivos.push(vencidos === 1 ? '1 documento vencido' : `${vencidos} documentos vencidos`);
    gravidade = 'critica';
  }
  if (vencendo > 0) {
    motivos.push(
      vencendo === 1
        ? `1 documento vence em ${JANELA_DE_AVISO_EM_DIAS} dias`
        : `${vencendo} documentos vencem em ${JANELA_DE_AVISO_EM_DIAS} dias`,
    );
    if (gravidade === 'regular') {
      gravidade = 'atencao';
    }
  }

  if (
    pendenciaFinanceira &&
    pendenciaFinanceira !== 'SEM_PENDENCIA' &&
    pendenciaFinanceira !== 'PAGO'
  ) {
    motivos.push('Pendência financeira');
    if (gravidade === 'regular') {
      gravidade = 'atencao';
    }
  }

  if (semVinculo) {
    motivos.push('Sem credenciado vinculado');
    if (gravidade === 'regular') {
      gravidade = 'atencao';
    }
  }

  if (gravidade === 'regular') {
    return (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge
              variant="outline"
              className={`cursor-help gap-1 font-medium ${STATUS_TONE.success}`}
            >
              <ShieldCheck className="h-3 w-3" aria-hidden="true" />
              Regular
            </Badge>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs text-xs">
            <p className="mb-1 font-semibold">Regular</p>
            <p className="text-muted-foreground">
              Situação ativa e documentos com mais de {JANELA_DE_AVISO_EM_DIAS} dias para vencer.
              Nada a providenciar.
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  const critica = gravidade === 'critica';
  const tom = critica ? STATUS_TONE.destructive : STATUS_TONE.warning;
  const titulo = critica ? 'Irregularidade que bloqueia' : 'Requer acompanhamento';
  const explicacao = critica
    ? 'Não pode entrar em ordem de serviço nova: há situação suspensa ou bloqueada, ou documento já vencido. Regularize antes de vincular.'
    : `Ainda opera, mas tem prazo: documento vencendo em até ${JANELA_DE_AVISO_EM_DIAS} dias, situação inativa, falta de vínculo ou pendência financeira.`;

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="outline" className={`cursor-help gap-1 font-medium ${tom}`}>
            <AlertTriangle className="h-3 w-3" aria-hidden="true" />
            {motivos.length === 1 ? motivos[0] : `${motivos.length} pendências`}
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-xs">
          <p className="mb-1 font-semibold">{titulo}</p>
          <p className="text-muted-foreground mb-2">{explicacao}</p>
          <p className="mb-1 font-medium">Motivos:</p>
          <ul className="space-y-0.5">
            {motivos.map((motivo) => (
              <li key={motivo}>• {motivo}</li>
            ))}
          </ul>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/*
 * As listas de validade de cada entidade ficam em `./validades.ts`, e nao aqui:
 * modulo que exporta componente E funcao perde a atualizacao rapida do Vite. Mesma
 * razao de `nucleo/navegacao/apresentacao.ts` existir apartado.
 */
